import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiMapPin, FiPhone } from "react-icons/fi";
import ClientOnly from "~/components/shared-components/ClentOnly";
import { TempleLocation, useContactData } from "~/hook/useContactData";
import { useFontStyle } from "~/hook/useFontStyle";
import { useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";

const KundaldhamAartiOrder = ["office_hours", "mangla_aarati", "sandhya_aarati", "morning_darshan", "evening_darshan"];
const VadodaraAartiOrder = [
  "office_hours",
  "mangla_aarati",
  "shangar_aarati",
  "rajbhog_aarati",
  "sandhya_aarati",
  "shayan_aarati",
  "morning_darshan",
  "evening_darshan",
];

const TempleLocations = () => {
  const { medium, mediumInter, normalInter } = useFontStyle();
  const contactData = useContactData();
  const { t } = useTranslation();

  const { templeLocations, templeHours, pageContent } = contactData;
  const { KundalAartiTime, VadodaraAartiTime } = useAppSelector((state) => state.contact);

  const renderTempleCardStatic = (location: TempleLocation) => {
    const templeImage = location.id === "kundal-dham" ? "/images/kundalDham.png" : "/images/vadodara.png";

    return (
      <div
        key={location.id}
        className="bg-secondaryBackground rounded-2xl overflow-hidden shadow-lg border border-borderPrimary flex flex-col h-full"
      >
        {/* Temple Image Section */}
        <div className="relative w-full h-80 sm:h-96 overflow-hidden">
          <img
            src={templeImage}
            alt={location.fullName}
            className="w-full h-full object-cover scale-[135%] contact_us_image-scale 2xl:scale-[112%]"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder-temple.jpg";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>

        {/* Header Section */}
        <div className="p-4 sm:p-6 border-b border-borderPrimary flex-grow">
          <div className="flex items-center space-x-3 mb-3">
            <FiMapPin size={20} className="text-textHeadingColor flex-shrink-0" />
            <h3 className={cn("body_text_l text-textHeadingColor", medium)}>{location.fullName}</h3>
          </div>

          {/* Contact Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center space-x-3">
              <FiPhone size={16} className="text-textHeadingColor flex-shrink-0" />
              <a
                href={`tel:${location.phone.replace(/\s/g, "")}`}
                className={cn(
                  "text-textColorPrimary body_text_s hover:text-textHeadingColor transition-colors duration-200 cursor-pointer hover:underline",
                  normalInter,
                )}
              >
                {location.phone}
              </a>
            </div>
          </div>

          {/* Temple Hours */}
          <div className="bg-cardBg p-3 rounded-lg">
            <h4 className={cn("text-textHeadingColor body_text_s font-medium mb-2", mediumInter)}>Temple Hours</h4>
            <div className="space-y-1 body_text_xs">
              {(location.id === "kundal-dham" ? KundalAartiTime : VadodaraAartiTime)?.value &&
                (() => {
                  const order = location.id === "kundal-dham" ? KundaldhamAartiOrder : VadodaraAartiOrder;
                  const aartiData = (location.id === "kundal-dham" ? KundalAartiTime : VadodaraAartiTime)!.value as Record<string, any>;

                  return order
                    .filter((key) => aartiData[key])
                    .map((key) => {
                      const hours = aartiData[key];
                      return (
                        <div key={key} className="flex justify-between">
                          <span className={cn("text-textColorPrimary body_text_xs ", normalInter)}>
                            {/* {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} */}
                            {t(`contact_us.${key}`)}
                          </span>
                          <span className={cn("text-textHeadingColor text-start !font-robotomono !font-medium body_text_sm", mediumInter)}>
                            {hours.time ? hours.time : `${hours.start} - ${hours.end}`}
                          </span>
                        </div>
                      );
                    });
                })()}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="relative w-full h-64 sm:h-80 ">
          <iframe
            src={location.mapSrc}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`${location.fullName} Location`}
            className="w-full h-full"
          />
        </div>

        {/* Action Button */}
        <div className="p-4 sm:p-6 bg-cardBg">
          <a
            href={location.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center space-x-2 px-4 py-3 bg-textHeadingColor hover:bg-dailyDarshanChipColor text-white rounded-lg text-sm font-medium transition-colors duration-300"
          >
            <FiMapPin size={16} />
            <span className={cn("body_text_s", normalInter)}>Get Directions</span>
          </a>
        </div>
      </div>
    );
  };

  const renderTempleCard = (location: TempleLocation, index: number) => {
    // Define temple images based on location ID
    const templeImage = location.id === "kundal-dham" ? "/images/kundalDham.png" : "/images/vadodara.png";

    return (
      <motion.div
        key={location.id}
        initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
        className="bg-secondaryBackground rounded-2xl overflow-hidden shadow-lg border border-borderPrimary flex flex-col h-full"
      >
        {/* Temple Image Section */}
        <div className="relative w-full h-80 sm:h-96 overflow-hidden">
          <img
            src={templeImage}
            alt={location.fullName}
            className="w-full h-full object-cover  scale-[135%] contact_us_image-scale 2xl:scale-[112%]"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder-temple.jpg";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>

        {/* Header Section */}
        <div className="p-4 sm:p-6 border-b border-borderPrimary flex-grow">
          <div className="flex items-center space-x-3 mb-3">
            <FiMapPin size={20} className="text-textHeadingColor flex-shrink-0" />
            <h3 className={cn("body_text_l text-textHeadingColor", medium)}>{location.fullName}</h3>
          </div>

          {/* Contact Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center space-x-3">
              <FiPhone size={16} className="text-textHeadingColor flex-shrink-0" />
              <a
                href={`tel:${location.phone.replace(/\s/g, "")}`}
                className={cn(
                  "text-textColorPrimary body_text_s hover:text-textHeadingColor transition-colors duration-200 cursor-pointer hover:underline",
                  normalInter,
                )}
                // title={`Call ${location.fullName}`}
              >
                {location.phone}
              </a>
            </div>
          </div>

          {/* Temple Hours */}
          <div className="bg-cardBg p-3 rounded-lg">
            <h4 className={cn("text-textHeadingColor body_text_s font-medium mb-2", mediumInter)}>Temple Hours</h4>
            <div className="space-y-1 body_text_xs">
              {(location.id === "kundal-dham" ? KundalAartiTime : VadodaraAartiTime)?.value &&
                (() => {
                  // Define the exact order - only these items will be shown
                  // Get the order array and data for current location
                  const order = location.id === "kundal-dham" ? KundaldhamAartiOrder : VadodaraAartiOrder;
                  const aartiData = (location.id === "kundal-dham" ? KundalAartiTime : VadodaraAartiTime)!.value as Record<string, any>;

                  // Show only times listed in the order array, in that exact order
                  return order
                    .filter((key) => aartiData[key]) // Only include if data exists for this key
                    .map((key) => {
                      const hours = aartiData[key];
                      return (
                        <div key={key} className="flex justify-between">
                          <span className={cn("text-textColorPrimary body_text_xs ", normalInter)}>
                            {/* {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} */}
                            {t(`contact_us.${key}`)}
                          </span>
                          <span className={cn("text-textHeadingColor text-start !font-robotomono !font-medium body_text_sm", mediumInter)}>
                            {hours.time ? hours.time : `${hours.start} - ${hours.end}`}
                          </span>
                        </div>
                      );
                    });
                })()}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="relative w-full h-64 sm:h-80">
          <iframe
            src={location.mapSrc}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`${location.fullName} Location`}
            className="w-full h-full"
          />
        </div>

        {/* Action Button */}
        <div className="p-4 sm:p-6 bg-cardBg">
          <motion.a
            href={location.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full inline-flex items-center justify-center space-x-2 px-4 py-3 bg-textHeadingColor hover:bg-dailyDarshanChipColor text-white rounded-lg text-sm font-medium transition-colors duration-300"
          >
            <FiMapPin size={16} />
            <span className={cn("body_text_s", normalInter)}>Get Directions</span>
          </motion.a>
        </div>
      </motion.div>
    );
  };

  return (
    <ClientOnly
      fallback={
        <div className="max-w-7xl mx-auto px-4 py-2 mt-10">
          <div className="text-center mb-8 lg:mb-12">
            <h2 className={cn("h2_heading  text-textHeadingColor mb-4 lg:mb-6", medium)}>{pageContent.templeLocations.title}</h2>
            <p className={cn("text-textColorPrimary body_text_l max-w-4xl mx-auto", normalInter)}>{pageContent.templeLocations.description}</p>
          </div>

          {/* Temple Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16 lg:mb-20 items-stretch">
            {templeLocations.map((location) => renderTempleCardStatic(location))}
          </div>
        </div>
      }
    >
      <div className="max-w-7xl mx-auto px-4 py-2 mt-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mb-8 lg:mb-12"
        >
          <h2 className={cn("h2_heading  text-textHeadingColor mb-4 lg:mb-6", medium)}>{pageContent.templeLocations.title}</h2>
          <p className={cn("text-textColorPrimary body_text_l max-w-4xl mx-auto", normalInter)}>{pageContent.templeLocations.description}</p>
        </motion.div>

        {/* Temple Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16 lg:mb-20 items-stretch">
          {templeLocations.map((location, index) => renderTempleCard(location, index))}
        </div>
      </div>
    </ClientOnly>
  );
};

export default TempleLocations;
